import { ChangeDetectorRef, ChangeDetectionStrategy, Component, input, signal, OnInit, ViewChild, output, effect, model, inject } from '@angular/core';
import { DropDownListComponent, DropDownsModule, PreventableEvent, VirtualizationSettings } from '@progress/kendo-angular-dropdowns';
import { IconsModule } from '@progress/kendo-angular-icons';
import { chevronDownIcon } from '@progress/kendo-svg-icons';
import { ODataService } from '../../services/odata.service';
import { Subscription } from 'rxjs';
import { takeUntil, tap } from 'rxjs/operators';
import { BaseComponent } from '../../shared/common/base.component';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { Router, ActivatedRoute } from '@angular/router';

@Component({
  selector: 'cyflare-virtual-dropdown',
  standalone: true,
  imports: [CommonModule, DropDownsModule, IconsModule],
  templateUrl: './virtual-dropdown.component.html',
  styleUrls: ['./virtual-dropdown.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: VirtualDropdownComponent,
      multi: true,
    },
  ],
})
export class VirtualDropdownComponent<T> extends BaseComponent implements OnInit, ControlValueAccessor {
  @ViewChild('dropdownList', { static: false }) dropdownList: DropDownListComponent | undefined;

  private readonly cdr = inject(ChangeDetectorRef);
  public sortField = input<string>('name');
  public sortOrder = input<any>('asc');
  public valueField = input<string>('id');
  public textField = input<string>('name');
  public filterField = input<string>('name');
  public refreshOnClick = input<boolean>(false);
  public dataService = input.required<ODataService<T>>();
  public styles = input<Record<string, any>>({ width: '225px' });
  public valueChanged = output<T>();
  public filterable = input<boolean>(false);
  public virtual = input<any>({ itemHeight: 36 });
  public value = model<T | undefined>();
  public mapper = input<(data: any[]) => any[]>();
  public defaultItem = input<any>({ id: 0, name: 'Select...' });
  public valuePrimitive = input<boolean>(false);
  public filterLength = input<number>(3);

  public data = signal<T[]>([]);
  public chevronDownIcon = chevronDownIcon;
  private page = 0;
  private totalRecords = 0;
  private readonly pageSize = 10;
  private scrollSubscription: Subscription | null = null;
  private isDisabled = false;
  private isLoading = false;

  private onChange: (value: T | undefined) => void = () => {};
  private onTouched: () => void = () => {};

  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);

  constructor() {
    super();
    this.initEffects();
  }

  ngOnInit(): void {
    this.getData();
  }

  ngAfterViewInit(): void {
    this.applyDisabledState();
  }

  ngOnChanges(): void {
    this.applyDisabledState();
  }

  private applyDisabledState(): void {
    if (this.dropdownList) {
      this.dropdownList.disabled = this.isDisabled;
      this.cdr.detectChanges(); // Manually trigger change detection
    }
  }

  private initEffects(): void {
    effect(() => {
      const defaultItem = this.defaultItem();
      if (defaultItem && this.dropdownList) {
        this.dropdownList.defaultItem = defaultItem;
      }
    });
  }

  addOrRemoveFilter(filter: any) {
    let currentFilter = this.dataService().state.filter;
    if (!currentFilter) {
      currentFilter = { logic: 'and', filters: [] };
    }

    const index = currentFilter.filters.findIndex((f: any) => f.field === filter.field);

    if (index === -1) {
      currentFilter.filters.push(filter);
    } else if (filter.value) {
      currentFilter.filters[index] = filter;
    } else {
      currentFilter.filters.splice(index, 1);
    }

    this.dataService().state = {
      skip: 0,
      take: this.pageSize,
      filter: currentFilter,
      sort: [
        {
          field: this.sortField(),
          dir: this.sortOrder(),
        },
      ],
    };
  }

  onDropdownFilterChange(value: any) {
    if (value.length >= this.filterLength()) {
      this.addOrRemoveFilter({
        field: this.filterField(),
        operator: 'contains',
        value: value,
      });
      this.page = 0;
      this.getData();
    } else if (value.length === 0) {
      this.addOrRemoveFilter({
        field: this.filterField(),
        operator: 'contains',
      });
      this.page = 0;
      this.getData();
    }
  }

  onSelectionChange(event: any): void {
    const selectedValue = event.dataItem;
    this.onDropdownChange(selectedValue);
  }

  onDropdownChange(event: T) {
    this.valueChanged.emit(event);
    this.onChange(event);
    this.onTouched();

    // If we're on the detection-settings route, emit a special event
    if (this.router.url.includes('detection-settings')) {
      const selectedOrgId = (event as any)[this.valueField()];

      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: { organizationId: selectedOrgId, refresh: new Date().getTime() },
        queryParamsHandling: 'merge',
      });
    }
  }

  public onDropdownOpened($event: any) {
    console.log('Dropdown opened');
    
    // Only reset and reload if we don't already have data or if refreshOnClick is true
    if (this.data().length === 0 || this.refreshOnClick()) {
      // Reset paging state but don't clear data immediately
      this.page = 0;
      this.totalRecords = 0;
      
      // Make sure the dataService state is properly initialized
      if (this.dataService() && this.dataService().state) {
        this.dataService().state.skip = 0;
        this.dataService().state.take = this.pageSize;
        
        // Ensure we have a proper filter
        if (!this.dataService().state.filter) {
          this.dataService().state.filter = {
            logic: 'and',
            filters: []
          };
        }
      }
      
      // Get data and only then subscribe to scroll
      this.getData(() => {
        console.log('Initial data loaded, subscribing to scroll');
        this.subscribeToScroll();
      });
    } else {
      // If we already have data, just subscribe to scroll
      this.subscribeToScroll();
    }
  }

  onDropdownOpen(event: PreventableEvent) {
    // if (this.refreshOnClick()) this.getData();
  }

  private subscribeToScroll(): void {
    if (this.dropdownList?.optionsList?.popupListScroll) {
      this.scrollSubscription?.unsubscribe();

      this.scrollSubscription = this.dropdownList.optionsList.popupListScroll
        .pipe(takeUntil(this.destroyed$))
        .subscribe(this.onScroll.bind(this));
    } else {
      console.log('Popup list scroll not available');
    }
  }

  private getData(callback?: () => void): void {
    // Don't start another request if one is already in progress
    if (this.isLoading) {
      console.log('Skipping getData call - already loading');
      return;
    }
    
    console.log('getData called, page:', this.page, 'pageSize:', this.pageSize);
    this.isLoading = true;
    
    this.dataService().state.skip = this.page * this.pageSize;
    this.dataService().state.take = this.pageSize;
    
    this.dataService().read((response: any) => {
      console.log('Data received:', response);
      this.totalRecords = response.total || response['@odata.count'] || 0;
      console.log('Total records:', this.totalRecords);
      
      if (this.mapper()) {
        response.data = this.mapper()!(response.data);
      }

      // Only clear existing data if we're on the first page and refreshOnClick is true
      if (this.page === 0 && this.refreshOnClick()) {
        this.data.set([...response.data]);
      } else if (this.page === 0) {
        // If we're on first page but not refreshing, only set if empty
        if (this.data().length === 0) {
          this.data.set([...response.data]);
        }
      } else {
        // For subsequent pages, always append
        this.data.update(existing => [...existing, ...response.data]);
      }

      this.isLoading = false;
      callback?.();
    });
  }

  private onScroll(event: any) {
    const listContainer = event.target as HTMLElement;
    const scrollTop = listContainer.scrollTop;
    const scrollHeight = listContainer.scrollHeight;
    const offsetHeight = listContainer.offsetHeight;
    
    console.log('Scroll detected:', { 
      scrollTop, 
      scrollHeight, 
      offsetHeight, 
      threshold: scrollTop + offsetHeight,
      currentItems: this.data().length,
      totalRecords: this.totalRecords
    });

    // Only load more data if:
    // 1. We're near the bottom of the scroll container
    // 2. We haven't loaded all records yet
    if (scrollTop + offsetHeight >= scrollHeight - 20 && 
        this.data().length < this.totalRecords) {
      console.log('Loading more data, page:', this.page + 1);
      this.page++;
      this.getData();
    }
  }

  writeValue(obj: T | undefined): void {
    Promise.resolve().then(() => {
      this.value.set(obj);
    });
  }

  registerOnChange(fn: (value: T | undefined) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
    this.applyDisabledState();
  }

  onItemClick(dataItem: T): void {
    this.valueChanged.emit(dataItem);
    this.onChange(dataItem);
    this.onTouched();
  }
}
